import Vue from 'vue';
import VueRouter from 'vue-router';
import { default as StudentLevels } from '@/views/Users/Student/Levels';
import { default as TeacherLevels } from '@/views/Users/Teacher/Levels';
import { RouteNameEnum } from '@/router/RouteName';
import { beforeResolveGuard } from '@/router/guards/beforeResolveGuard';
import { beforeEachGuard } from '@/router/guards/beforeEachGuard';
import { authMiddleware } from '@/router/middleware/authMiddleware';
import { guestMiddleware } from '@/router/middleware/guestMiddleware';
import { initializeStoreMiddleware } from '@/router/middleware/initializeStoreMiddleware';
import { roleBasedRedirectMiddleware } from '@/router/middleware/roleBasedRedirectMiddleware';
import 'vuetify/dist/vuetify.min.css';
import { RolesEnum } from '@/items/Roles';
import { globalUserMiddleware } from './middleware/globalUserMiddleware';
import store from '@/store';
Vue.use(VueRouter);
export const doLogGuards = false;
export var MiddlewareGroupEnum;
(function (MiddlewareGroupEnum) {
    MiddlewareGroupEnum["PUBLIC"] = "public";
    MiddlewareGroupEnum["PRIVATE"] = "private";
    MiddlewareGroupEnum["PRIVATE_GLOBAL"] = "private-global";
    MiddlewareGroupEnum["GUEST"] = "guest";
})(MiddlewareGroupEnum || (MiddlewareGroupEnum = {}));
export const middlewareGroups = {
    [MiddlewareGroupEnum.PUBLIC]: [initializeStoreMiddleware],
    [MiddlewareGroupEnum.PRIVATE]: [
        authMiddleware,
        roleBasedRedirectMiddleware,
        initializeStoreMiddleware,
    ],
    [MiddlewareGroupEnum.PRIVATE_GLOBAL]: [
        authMiddleware,
        roleBasedRedirectMiddleware,
        globalUserMiddleware,
        initializeStoreMiddleware,
    ],
    [MiddlewareGroupEnum.GUEST]: [guestMiddleware, initializeStoreMiddleware],
};
const router = new VueRouter({
    mode: 'history',
    routes: [
        {
            path: '/',
            name: RouteNameEnum.HOME,
            component: () => import(/* webpackChunkName: "home" */ '@/views/Dashboard/Dashboard'),
        },
        {
            path: '/logs',
            name: 'logs',
            meta: { middleware: [MiddlewareGroupEnum.PRIVATE_GLOBAL] },
            component: () => import(/* webpackChunkName: "home" */ '@/views/Logs/Logs'),
        },
        {
            path: '/admin',
            name: 'admin',
            component: () => import(/* webpackChunkName: "home" */ '@/views/Admin/Admin'),
            redirect: { name: 'admin.dashboard' },
            meta: { middleware: [MiddlewareGroupEnum.PRIVATE_GLOBAL] },
            children: [
                {
                    path: 'dashboard',
                    name: 'admin.dashboard',
                    meta: { middleware: [MiddlewareGroupEnum.PRIVATE_GLOBAL] },
                    component: () => import(
                    /* webpackChunkName: "admin.dashboard" */ '@/views/Admin/Dashboard/Dashboard.vue'),
                },
                {
                    path: 'users',
                    name: 'users',
                    meta: { middleware: [MiddlewareGroupEnum.PRIVATE_GLOBAL] },
                    component: () => import(/* webpackChunkName: "users" */ '@/views/Admin/Users/Users'),
                },
                {
                    path: 'users/:id',
                    name: 'user',
                    meta: { middleware: [MiddlewareGroupEnum.PRIVATE_GLOBAL] },
                    component: () => import(/* webpackChunkName: "user" */ '@/views/Admin/Users/User'),
                },
                {
                    path: 'schools',
                    name: 'schools.index',
                    meta: { middleware: [MiddlewareGroupEnum.PRIVATE_GLOBAL] },
                    component: () => import(/* webpackChunkName: "schools" */ '@/views/Admin/Schools/Schools'),
                },
                {
                    path: 'whitelabels',
                    name: 'admin.whitelabels',
                    meta: { middleware: [MiddlewareGroupEnum.PRIVATE_GLOBAL] },
                    component: () => import(/* webpackChunkName: "whitelabels" */ '@/views/Admin/Whitelabels/Whitelabels'),
                },
                {
                    path: 'whitelabels/:id',
                    name: 'admin.whitelabel',
                    meta: { middleware: [MiddlewareGroupEnum.PRIVATE_GLOBAL] },
                    component: () => import(/* webpackChunkName: "whitelabel" */ '@/views/Admin/Whitelabels/Whitelabel'),
                },
                {
                    path: 'year-templates',
                    name: 'admin.yearTemplates',
                    meta: { middleware: [MiddlewareGroupEnum.PRIVATE_GLOBAL] },
                    component: () => import(/* webpackChunkName: "yearTemplates" */ '@/views/Admin/YearTemplates/YearTemplates'),
                },
                {
                    path: 'year-templates/:id',
                    name: 'admin.yearTemplate',
                    meta: { middleware: [MiddlewareGroupEnum.PRIVATE_GLOBAL] },
                    component: () => import(/* webpackChunkName: "yearTemplate" */ '@/views/Admin/YearTemplates/YearTemplate'),
                },
            ],
        },
        {
            path: '/organizations',
            name: 'organizations',
            component: () => import(
            /* webpackChunkName: "organizations.index" */ '@/views/Organizations/Organizations.vue'),
        },
        {
            path: '/organizations/:id',
            name: 'organizations.detail',
            component: () => import(
            /* webpackChunkName: "organizations.detail" */ '@/views/Organizations/Organization.vue'),
        },
        {
            path: '/tags',
            name: 'tags',
            component: () => import(/* webpackChunkName: "tags" */ '@/views/Tags/Tags'),
        },
        {
            path: '/notification-history',
            name: 'notification.history',
            component: () => import(
            /* webpackChunkName: "notification-history" */ '@/views/NotificationHistory/NotificationHistory'),
        },
        {
            path: '/students',
            name: 'students.index',
            component: () => import(/* webpackChunkName: "students" */ '@/views/Users/Users'),
            meta: { type: 'Student' },
        },
        {
            path: '/groups/:group_id/students/:id',
            name: 'student.group',
            component: () => import(/* webpackChunkName: "student" */ '@/views/Users/Student/Student'),
            children: [
                {
                    path: '',
                    name: 'students.group.home',
                    component: () => import(/* webpackChunkName: "idp" */ '@/views/Users/Student/Home'),
                },
                {
                    path: 'school-tracker',
                    name: 'students.group.show',
                    component: () => import(/* webpackChunkName: "idp" */ '@/views/Users/Student/Idps/Idp'),
                },
                {
                    path: 'subject/:subjectid',
                    name: 'subject.group.idp',
                    component: () => import(/* webpackChunkName: "studentSubject" */ '@/views/Subjects/Summary'),
                },
                // SCOOL-476: portfolio menu item disabled for all schools (unused, gives an error).
                // Route kept (commented) until the customer decides to remove it permanently.
                // {
                //   path: 'portfolio',
                //   name: 'students.group.portfolio',
                //   component: () =>
                //     import(/* webpackChunkName: "scoolfolio" */ '@/views/Users/Student/Scoolfolio'),
                // },
                {
                    path: 'this-is-me',
                    name: 'students.group.this-is-me',
                    component: () => import(/* webpackChunkName: "this-is-me" */ '@/views/Users/Student/Progress/Progress'),
                },
                {
                    path: 'messages',
                    name: 'students.group.messages',
                    component: () => import(/* webpackChunkName: "messages" */ '@/views/Users/Student/Messages'),
                },
                {
                    path: 'finished',
                    name: 'student.finished',
                    component: () => import(/* webpackChunkName: "finished" */ '@/views/Users/Student/Finished'),
                },
                {
                    path: 'levels',
                    name: 'students.group.levels',
                    component: StudentLevels,
                },
                {
                    path: 'profile',
                    name: 'students.group.profile',
                    component: () => import(/* webpackChunkName: "profile" */ '@/views/Users/Student/Profile'),
                },
                {
                    path: 'notes',
                    name: 'students.group.notes',
                    component: () => import(/* webpackChunkName: "studentNotes" */ '@/views/Users/Student/Notes'),
                },
                {
                    path: 'roster',
                    name: 'students.group.roster',
                    component: () => import(/* webpackChunkName: "studentRoster" */ '@/views/Users/Student/Roster'),
                },
                {
                    path: 'treatment',
                    name: 'students.group.treatment',
                    component: () => import(/* webpackChunkName: "studentTreatment" */ '@/views/Users/Student/Treatment'),
                },
                {
                    path: 'plan',
                    name: 'students.group.plan',
                    component: () => import(/* webpackChunkName: "studentLvs" */ '@/views/Users/Student/Plan'),
                },
                {
                    path: 'lvs',
                    name: 'students.group.lvs',
                    component: () => import(/* webpackChunkName: "studentLvs" */ '@/views/Users/Student/Lvs'),
                },
            ],
        },
        {
            path: '/students/:id',
            component: () => import(/* webpackChunkName: "student" */ '@/views/Users/Student/Student'),
            children: [
                {
                    path: '',
                    name: 'students.home',
                    component: () => import(/* webpackChunkName: "idp" */ '@/views/Users/Student/Home'),
                },
                {
                    path: 'school-tracker',
                    name: 'students.show',
                    component: () => import(/* webpackChunkName: "idp" */ '@/views/Users/Student/Idps/Idp'),
                },
                {
                    path: 'subject/:subjectid',
                    name: 'subject.idp',
                    component: () => import(/* webpackChunkName: "studentSubject" */ '@/views/Subjects/Summary'),
                },
                // SCOOL-476: portfolio menu item disabled for all schools (unused, gives an error).
                // Route kept (commented) until the customer decides to remove it permanently.
                // {
                //   path: 'portfolio',
                //   component: () =>
                //     import(/* webpackChunkName: "scoolfolio" */ '@/views/Users/Student/Scoolfolio'),
                // },
                {
                    path: 'this-is-me',
                    component: () => import(/* webpackChunkName: "this-is-me" */ '@/views/Users/Student/Progress/Progress'),
                },
                {
                    path: 'messages',
                    component: () => import(/* webpackChunkName: "messages" */ '@/views/Users/Student/Messages'),
                },
                {
                    path: 'finished',
                    name: RouteNameEnum.STUDENT_FINISHED,
                    component: () => import(/* webpackChunkName: "finished" */ '@/views/Users/Student/Finished'),
                },
                {
                    path: 'levels',
                    component: StudentLevels,
                },
                {
                    path: 'profile',
                    component: () => import(/* webpackChunkName: "profile" */ '@/views/Users/Student/Profile'),
                },
                {
                    path: 'notes',
                    component: () => import(/* webpackChunkName: "studentNotes" */ '@/views/Users/Student/Notes'),
                },
                {
                    path: 'roster',
                    component: () => import(/* webpackChunkName: "studentRoster" */ '@/views/Users/Student/Roster'),
                },
                {
                    path: 'treatment',
                    component: () => import(/* webpackChunkName: "studentTreatment" */ '@/views/Users/Student/Treatment'),
                },
                {
                    path: 'plan',
                    component: () => import(/* webpackChunkName: "studentLvs" */ '@/views/Users/Student/Plan'),
                },
                {
                    path: 'lvs',
                    component: () => import(/* webpackChunkName: "studentLvs" */ '@/views/Users/Student/Lvs'),
                },
            ],
        },
        {
            path: '/teachers',
            name: 'teachers.index',
            component: () => import(/* webpackChunkName: "teachers" */ '@/views/Users/Users'),
            meta: { type: 'Teacher' },
        },
        {
            path: '/teachers/:id',
            component: () => import(/* webpackChunkName: "teacher" */ '@/views/Users/Teacher/Teacher'),
            children: [
                {
                    path: '',
                    component: () => import(/* webpackChunkName: "teacherProfile" */ '@/views/Users/Teacher/Profile'),
                },
                {
                    path: 'hours',
                    component: () => import(/* webpackChunkName: "teacherHour" */ '@/views/Users/Teacher/ContractHours'),
                },
                {
                    path: 'levels',
                    component: TeacherLevels,
                },
                {
                    path: 'calendar',
                    component: () => import(/* webpackChunkName: "teacherCalendar" */ '@/views/Users/Teacher/Calendar'),
                },
            ],
        },
        {
            path: '/guardians',
            name: 'guardians.index',
            component: () => import(/* webpackChunkName: "guardians" */ '@/views/Users/Users'),
            meta: { type: 'Guardian' },
        },
        {
            path: '/guardians/:id',
            name: 'guardians.show',
            component: () => import(/* webpackChunkName: "guardian" */ '@/views/Users/Guardian/Guardian'),
        },
        {
            path: '/supervisors',
            name: 'supervisors.index',
            component: () => import(/* webpackChunkName: "supervisors" */ '@/views/Users/Users'),
            meta: { type: 'Supervisor' },
        },
        {
            path: '/supervisors/:id',
            name: 'supervisors.show',
            component: () => import(/* webpackChunkName: "supervisor" */ '@/views/Users/Supervisor/Supervisor'),
        },
        {
            path: '/administrators',
            name: 'administrators.index',
            meta: { type: 'Administrator' },
            component: () => import(/* webpackChunkName: "administrators" */ '@/views/Users/Users'),
        },
        {
            path: '/administrators/:id',
            name: 'administrators.show',
            component: () => import(/* webpackChunkName: "administrator" */ '@/views/Users/Administrator/Administrator'),
        },
        {
            path: 'super-administrators',
            name: 'super-administrators.index',
            meta: { type: 'Root' },
            component: () => import(/* webpackChunkName: "superAdministrators" */ '@/views/Users/Users'),
        },
        {
            path: 'super-administrators/:id',
            name: 'super-administrators.show',
            component: () => import(/* webpackChunkName: "root" */ '@/views/Users/Root/Root'),
        },
        {
            path: '/specialists',
            name: 'specialists.index',
            component: () => import(/* webpackChunkName: "specialists" */ '@/views/Users/Users'),
            meta: { type: 'Specialist' },
        },
        {
            path: '/specialists/:id',
            name: 'specialists.show',
            component: () => import(/* webpackChunkName: "specialist" */ '@/views/Users/Specialist/Specialist'),
        },
        {
            path: '/trainers',
            name: 'trainers.index',
            component: () => import(/* webpackChunkName: "trainers" */ '@/views/Users/Users'),
            meta: { type: 'Trainer' },
        },
        {
            path: '/trainers/:id',
            name: 'trainers.show',
            component: () => import(/* webpackChunkName: "trainer" */ '@/views/Users/Trainer/Trainer'),
        },
        {
            path: '/login',
            name: RouteNameEnum.LOGIN,
            component: () => import(/* webpackChunkName: "login" */ '@/views/Auth/Login'),
            meta: { middleware: [MiddlewareGroupEnum.PUBLIC] },
        },
        {
            path: '/forgot-password',
            name: RouteNameEnum.FORGOT_PASSWORD,
            component: () => import(/* webpackChunkName: "forgotPassword" */ '@/views/ForgotPassword/ForgotPassword'),
            meta: { middleware: [MiddlewareGroupEnum.GUEST] },
        },
        {
            path: '/reset-password',
            name: RouteNameEnum.RESET_PASSWORD,
            component: () => import(/* webpackChunkName: "resetPassword" */ '@/views/ResetPassword/ResetPassword'),
            meta: { middleware: [MiddlewareGroupEnum.GUEST] },
        },
        {
            path: '/set-mfa',
            name: RouteNameEnum.SET_MFA,
            component: () => import(/* webpackChunkName: "set-mfa" */ '@/views/Auth/SetMfaPage/SetMfaPage'),
            meta: { middleware: [MiddlewareGroupEnum.PUBLIC] },
        },
        {
            path: '/curricula',
            name: 'curricula',
            component: () => import(/* webpackChunkName: "curricula" */ '@/views/Curricula/Curricula'),
        },
        {
            path: '/curricula/skill-sets',
            name: 'skill-sets',
            component: () => import(/* webpackChunkName: "skill-sets" */ '@/views/Curricula/SkillSets'),
        },
        {
            path: '/curricula/:id',
            name: RouteNameEnum.CURRICULUM,
            component: () => import(/* webpackChunkName: "curriculum" */ '@/views/Curricula/Curriculum'),
        },
        {
            path: '/levels',
            name: 'levels.index',
            component: () => import(/* webpackChunkName: "levels" */ '@/views/Levels/Levels'),
        },
        {
            path: '/graduation-profiles',
            name: 'graduation-profiles.index',
            component: () => import(
            /* webpackChunkName: "graduation-profiles" */ '@/views/GraduationProfiles/GraduationProfiles'),
        },
        {
            path: '/levels/:id',
            name: 'levels.show',
            component: () => import(/* webpackChunkName: "level" */ '@/views/Levels/Level'),
        },
        {
            path: '/subject-label-categories',
            name: 'subject-label-categories.index',
            component: () => import(
            /* webpackChunkName: "subject-label-categories" */ '@/views/SubjectLabelCategories/SubjectLabelCategories'),
        },
        {
            path: '/subject-label-categories/:id',
            name: 'subject-label-category.index',
            component: () => import(
            /* webpackChunkName: "subject-labels" */ '@/views/SubjectLabelCategory/SubjectLabelCategory'),
        },
        {
            path: '/project-categories',
            name: 'project-categories.index',
            component: () => import(
            /* webpackChunkName: "project-categories" */ '@/views/Projects/ProjectCategories.vue'),
        },
        {
            path: '/my-project-categories',
            name: 'project.mine',
            component: () => import(
            /* webpackChunkName: "my-project-categories" */ '@/views/Projects/ProjectCategories.vue'),
            meta: { myProjectCategories: true },
        },
        {
            path: '/project-categories/:id',
            name: 'project-category.index',
            component: () => import(/* webpackChunkName: "projects" */ '@/views/Projects/Projects'),
        },
        // {
        //   path: '/projects',
        //   name: 'projects.index',
        //   component: () => import(/* webpackChunkName: "projects" */ '@/views/Projects/Projects'),
        // },
        // TO DO: what to do with 'my projects'?
        // {
        //   path: '/my-projects',
        //   name: 'project.mine',
        //   component: () => import(/* webpackChunkName: "myProjects" */ '@/views/Projects/Projects'),
        //   meta: { myProject: true },
        // },
        {
            path: '/plan-templates',
            name: 'plan.templates',
            component: () => import(/* webpackChunkName: "planTemplates" */ '@/views/PlanTemplates/PlanTemplates.vue'),
        },
        {
            path: '/plan-templates/:id',
            name: 'plan.templates',
            component: () => import(/* webpackChunkName: "planTemplates" */ '@/views/PlanTemplates/PlanTemplate.vue'),
        },
        {
            path: '/projects/:id',
            component: () => import(/* webpackChunkName: "project" */ '@/views/Projects/Project'),
            children: [
                {
                    path: '',
                    component: () => import(/* webpackChunkName: "projectHome" */ '@/views/Projects/Home'),
                },
                {
                    path: 'subjects',
                    component: () => import(
                    /* webpackChunkName: "projectSubjects" */ '@/views/Projects/Tabs/Subjects/Subjects'),
                },
                {
                    path: 'project-plan',
                    name: 'project.project-plan',
                    component: () => import(
                    /* webpackChunkName: "projectPlan" */ '@/views/Projects/Tabs/ProjectPlan/ProjectPlan'),
                },
                {
                    path: 'files',
                    name: 'project.files',
                    component: () => import(/* webpackChunkName: "projectFiles" */ '@/views/Projects/Tabs/Files/Files'),
                },
                {
                    path: 'notes',
                    name: 'project.notes',
                    component: () => import(/* webpackChunkName: "projectNotes" */ '@/views/Projects/Tabs/Notes/Notes'),
                },
                // { path: 'messenger', name: 'project.messenger', component: () => import(/* webpackChunkName: "projectMessenger" */ '@/views/Projects/Tabs/Messenger/Messenger') },
                {
                    path: 'media',
                    component: () => import(/* webpackChunkName: "projectMedia" */ '@/views/Projects/Tabs/Media/Media'),
                },
                {
                    path: 'documents',
                    component: () => import(
                    /* webpackChunkName: "projectDocuments" */ '@/views/Projects/Tabs/Documents/Documents.vue'),
                },
            ],
        },
        {
            path: '/groups',
            name: 'groups',
            component: () => import(/* webpackChunkName: "groups" */ '@/views/Groups/Groups'),
        },
        {
            path: '/groups/mine',
            name: 'mine',
            component: () => import(/* webpackChunkName: "myGroups" */ '@/views/Groups/Groups'),
            meta: { myGroup: true },
        },
        {
            path: '/groups/:id',
            component: () => import(/* webpackChunkName: "group" */ '@/views/Groups/Group'),
            children: [
                {
                    path: '',
                    component: () => import(/* webpackChunkName: "groupProfile" */ '@/views/Groups/Home'),
                },
                {
                    path: 'profile',
                    component: () => import(/* webpackChunkName: "groupProfile" */ '@/views/Groups/Tabs/Profile'),
                },
                {
                    path: 'curriculum',
                    name: 'groups.idp',
                    component: () => import(/* webpackChunkName: "groupIdp" */ '@/views/Groups/Tabs/Idp'),
                },
                {
                    path: 'messages',
                    name: 'groups.messages',
                    component: () => import(/* webpackChunkName: "groupMessages" */ '@/views/Groups/Tabs/Messages'),
                },
                {
                    path: 'notes',
                    name: 'groups.notes',
                    component: () => import(/* webpackChunkName: "groupNotes" */ '@/views/Groups/Tabs/Notes'),
                },
                {
                    path: 'files',
                    name: 'groups.files',
                    component: () => import(/* webpackChunkName: "groupFiles" */ '@/views/Groups/Tabs/Files'),
                },
                {
                    path: 'steps',
                    name: 'groups.steps',
                    component: () => import(/* webpackChunkName: "groupSteps" */ '@/views/Groups/Tabs/Steps'),
                },
                {
                    path: 'audit',
                    name: 'groups.audit',
                    component: () => import(/* webpackChunkName: "groupAudit" */ '@/views/Groups/Tabs/Audit'),
                },
                // { path: 'projectplan', name: 'groups.projectPlan', component: () => import(/* webpackChunkName: "groupProjectPlan" */ '@/views/Groups/Tabs/ProjectPlan') },
                {
                    path: 'projects',
                    name: 'groups.projects',
                    component: () => import(/* webpackChunkName: "groupProjects" */ '@/views/Groups/Projects/Projects.vue'),
                },
                {
                    path: 'report',
                    name: 'groups.report',
                    component: () => import(/* webpackChunkName: "groupReport" */ '@/views/Groups/Tabs/Report'),
                },
                {
                    path: 'lvs',
                    name: 'groups.lvs',
                    component: () => import(/* webpackChunkName: "groupLvs" */ '@/views/Groups/Tabs/Report'),
                },
                {
                    path: 'subject/:subjectid',
                    name: 'subject.lvs',
                    component: () => import(/* webpackChunkName: "groupSubject" */ '@/views/Subjects/Summary'),
                },
                {
                    path: 'student-overview',
                    name: 'groups.student-overview',
                    component: () => import(
                    /* webpackChunkName: "groupStudentOverview" */ '@/views/Groups/Tabs/StudentOverview/StudentOverview'),
                },
                {
                    path: 'plan',
                    name: 'groups.plan',
                    component: () => import(/* webpackChunkName: "groupPlan" */ '@/views/Groups/Tabs/Plan/Plan'),
                },
                {
                    path: 'student-list',
                    name: 'groups.student-list',
                    component: () => import(
                    /* webpackChunkName: "groupStudentList" */ '@/views/Groups/Tabs/StudentList/StudentList'),
                },
                {
                    path: 'actions',
                    name: 'groups.actions',
                    component: () => import(/* webpackChunkName: "groupActions" */ '@/views/Groups/Tabs/Actions/Actions'),
                },
                {
                    path: 'goal-planner',
                    name: 'groups.goal-planner',
                    component: () => import(
                    /* webpackChunkName: "groupGoalPlanner" */ '@/views/Groups/Tabs/GoalPlanner/GoalPlanner.vue'),
                },
            ],
        },
        {
            path: '/goal-planner/:groupId/timeslots/:id',
            name: 'goal-planner.timeslot',
            component: () => import(
            /* webpackChunkName: "timeslotGoalOverview" */ '@/views/Groups/Tabs/GoalPlanner/TimeslotGoalOverview/TimeslotGoalOverview.vue'),
        },
        {
            path: '/documents',
            name: 'documents',
            component: () => import(/* webpackChunkName: "noTenant" */ '@/views/Documents/Documents'),
        },
        {
            path: '/goal-planner/templates',
            name: 'goal-planner.templates',
            component: () => import(/* webpackChunkName: "noTenant" */ '@/views/GoalPlanner/Templates/Templates.vue'),
        },
        {
            path: '/goal-planner/templates/:id',
            name: 'goal-planner.template.index',
            component: () => import(/* webpackChunkName: "noTenant" */ '@/views/GoalPlanner/Template/Template.vue'),
        },
        {
            path: '/subject-overview',
            name: 'subject-overview.index',
            component: () => import(/* webpackChunkName: "subjects" */ '@/views/Subjects/Summary'),
        },
        {
            path: '/subjects',
            name: 'subjects.index',
            component: () => import(/* webpackChunkName: "subjects" */ '@/views/Subjects/Subject'),
        },
        {
            path: '/subjects/:id',
            name: 'subjects.show',
            component: () => import(/* webpackChunkName: "subject" */ '@/views/Subjects/Subject'),
        },
        {
            path: '/events/:id/subjects',
            name: 'events.subjects',
            component: () => import(/* webpackChunkName: "scheduleSubject" */ '@/views/Schedules/ScheduleSubject'),
        },
        {
            path: '/events/:id',
            name: 'events.show',
            component: () => import(/* webpackChunkName: "schedule" */ '@/views/Schedules/Schedule'),
        },
        {
            path: '/years',
            name: 'years',
            component: () => import(/* webpackChunkName: "years" */ '@/views/Years/Years'),
        },
        {
            path: '/years/:id',
            name: 'years.show',
            component: () => import(/* webpackChunkName: "year" */ '@/views/Years/Year'),
        },
        {
            path: '/periods',
            name: 'periods',
            component: () => import(/* webpackChunkName: "periods" */ '@/views/Periods/Periods'),
        },
        {
            path: '/periods/:id',
            name: 'periods.show',
            component: () => import(/* webpackChunkName: "period" */ '@/views/Periods/Period'),
        },
        {
            path: '/parnassys',
            name: 'parnassys',
            component: () => import(/* webpackChunkName: "parnassys" */ '@/views/Parnassys/Parnassys'),
            meta: { requiresRoles: [] },
        },
        {
            path: '/school-settings/:id',
            name: 'school-settings.show',
            // Or if is isTeacherWithExpertRights, but this is not handled here or in the middleware, but in the Navigation
            meta: {
                requiresRoles: [RolesEnum.ROOT, RolesEnum.ADMIN, RolesEnum.TRAINER],
            },
            redirect: (to) => {
                if (store.state.Auth && store.state.Auth.isTeacherWithExpertRights()) {
                    return { name: 'school-settings.show.settings' };
                }
                return { name: 'school-settings.show.profile' };
            },
            component: () => import(/* webpackChunkName: "school" */ '@/views/School/School'),
            children: [
                {
                    path: 'profile',
                    name: 'school-settings.show.profile',
                    component: () => import(
                    /* webpackChunkName: "school-settings--show-profile" */ '@/views/School/SchoolProfile/SchoolProfile'),
                },
                {
                    path: 'settings',
                    name: 'school-settings.show.settings',
                    component: () => import(
                    /* webpackChunkName: "school-settings--show-settings" */ '@/views/School/SchoolSettings/SchoolSettings'),
                },
                {
                    path: 'status-sets',
                    name: 'school-settings.show.status_sets',
                    component: () => import(
                    /* webpackChunkName: "school-settings--show-status-sets" */ '@/views/School/StatusSets/StatusSets'),
                },
                {
                    path: 'lvs-settings',
                    name: 'school-settings.show.lvs_settings',
                    component: () => import(
                    /* webpackChunkName: "school-settings--show-lvs_settings" */ '@/views/School/SchoolLvsSettings/SchoolLvsSettings'),
                },
                {
                    path: 'group-settings',
                    name: 'school-settings.show.group_settings',
                    component: () => import(
                    /* webpackChunkName: "school-settings--show-group_settings" */ '@/views/School/SchoolGroupSettings/SchoolGroupSettings'),
                },
                {
                    path: 'tools',
                    name: 'school-settings.show.tools',
                    component: () => import(
                    /* webpackChunkName: "school-settings--show-tools" */ '@/views/School/SchoolTools/SchoolTools'),
                },
                {
                    path: 'logboek',
                    meta: { middleware: [MiddlewareGroupEnum.PRIVATE_GLOBAL] },
                    name: 'school.show.tools',
                    component: () => import(
                    /* webpackChunkName: "school-show-tools" */ '@/views/School/SchoolLogs/SchoolLogs'),
                },
            ],
        },
        {
            path: '/rooms',
            name: 'rooms',
            component: () => import(/* webpackChunkName: "rooms" */ '@/views/Rooms/Rooms'),
        },
        {
            path: '/rooms/:id',
            name: 'rooms.show',
            component: () => import(/* webpackChunkName: "room" */ '@/views/Rooms/Room'),
        },
        {
            path: '/roster/settings',
            component: () => import(/* webpackChunkName: "roster" */ '@/views/Roster/Roster'),
            meta: {
                requiresRoles: [RolesEnum.ROOT, RolesEnum.ADMIN, RolesEnum.TRAINER],
            },
            children: [
                {
                    path: '',
                    component: () => import(/* webpackChunkName: "rosterSettings" */ '@/views/Roster/RosterSettings'),
                },
                {
                    path: 'holiday',
                    component: () => import(/* webpackChunkName: "holiday" */ '@/views/Roster/Holiday'),
                },
                // { path: 'settings', component: () => import(/* webpackChunkName: "settings" */ '@/views/Roster/Settings') },
            ],
        },
        {
            path: '/lesson-types',
            name: 'lesson-types.index',
            component: () => import(/* webpackChunkName: "lessonTypes" */ '@/views/LessonTypes/LessonTypes'),
        },
        {
            path: '/lesson-types/:id',
            name: 'lesson-types.show',
            component: () => import(/* webpackChunkName: "lessonType" */ '@/views/LessonTypes/LessonTypes'),
        },
        {
            path: '/roster/templates',
            name: 'roster.templates',
            component: () => import(/* webpackChunkName: "rosterTemplates" */ '@/views/RosterTemplates/RosterTemplates'),
        },
        {
            path: '/roster/templates/:id',
            name: 'roster.templates.show',
            component: () => import(/* webpackChunkName: "rosterTemplate" */ '@/views/RosterTemplates/RosterTemplate'),
        },
        {
            path: '/roster/management',
            name: 'roster.management',
            component: () => import(
            /* webpackChunkName: "rosterManagement" */ '@/views/RosterManagement/RosterManagement'),
        },
        {
            path: '/roster/management/notifications',
            name: 'roster.management.notifications',
            component: () => import(
            /* webpackChunkName: "rosterManagementNotifications" */ '@/views/RosterManagement/RosterNotifications'),
        },
        {
            path: '/roster/:id',
            name: 'user-roster',
            component: () => import(/* webpackChunkName: "userRoster" */ '@/views/Users/UserRoster'),
        },
        {
            path: '/goal-summary',
            name: 'subjects.summary',
            component: () => import(/* webpackChunkName: "summary" */ '@/views/Subjects/Summary'),
        },
        {
            path: '/absences',
            name: 'absences',
            component: () => import(/* webpackChunkName: "absences" */ '@/views/Stats/Absences/Absences'),
        },
        {
            path: '/help',
            name: 'help',
            component: () => import(/* webpackChunkName: "help" */ '@/views/Helps/Helps'),
            meta: { middleware: [MiddlewareGroupEnum.PUBLIC] },
        },
        {
            path: '/fact',
            name: 'fact',
            component: () => import(/* webpackChunkName: "fact" */ '@/views/Facts/Facts'),
            meta: { middleware: [MiddlewareGroupEnum.PUBLIC] },
        },
        {
            path: '*',
            name: RouteNameEnum.PAGE_NOT_FOUND,
            component: () => import(/* webpackChunkName: "pageNotFound" */ '@/views/Errors/PageNotFound'),
            meta: { middleware: [MiddlewareGroupEnum.PUBLIC] },
        },
        {
            path: '/api-offline',
            name: RouteNameEnum.PAGE_OFFLINE,
            component: () => import(/* webpackChunkName: "offline" */ '@/views/Offline/Offline.vue'),
            meta: { middleware: [MiddlewareGroupEnum.PUBLIC] },
        },
        {
            path: '/invalid',
            name: 'no.tenant',
            component: () => import(/* webpackChunkName: "noTenant" */ '@/views/Errors/NoTenant'),
            meta: { middleware: [MiddlewareGroupEnum.PUBLIC] },
        },
        // {
        //   path: '/calendar',
        //   name: 'calendar',
        //   component: Calendar,
        // },
    ],
});
router.beforeEach(beforeEachGuard);
router.beforeResolve(beforeResolveGuard);
export default router;
